/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HtmlCssWorkspaceModal — full-screen workspace editor for html_css_workspace sections.
 * UI is derived from the active Arcee theme (gradient toolbar, primary accents, light canvas).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { useTheme } from '../../theme/ThemeContext';
import { useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../../hooks/usePages';
import { useHtmlCssPreviewData } from '../../hooks/useHtmlCssPreviewData';
import { serializeArceeData, buildCardConfig, buildCardScript } from '../../utils/htmlCssPreviewData';
import { buildTemplateFiles, downloadTemplateZip, templateBlockedReason } from '../../utils/downloadTemplate';
import HtmlCssDataBindingsPanel from './HtmlCssDataBindingsPanel';
import HtmlCssLinkNavPanel from './HtmlCssLinkNavPanel';
import HtmlCssCardsPanel from './HtmlCssCardsPanel';
import { buildPreviewNavScript } from '../../utils/htmlLinkTargets';

const TABS = [
  { key: 'html',     label: 'HTML',          language: 'html',       icon: 'bi-filetype-html' },
  { key: 'css',      label: 'CSS',           language: 'css',        icon: 'bi-filetype-css'  },
  { key: 'js',       label: 'JS',            language: 'javascript', icon: 'bi-filetype-js'   },
  { key: 'bindings', label: 'Data Bindings', icon: 'bi-database' },
  { key: 'links',    label: 'Links',         icon: 'bi-signpost-2' },
  { key: 'cards',    label: 'Cards',         icon: 'bi-hand-index-thumb' },
];

const SANDBOX_LIBS = [
  { label: 'Bootstrap',       version: '5.3.2', icon: 'bi-bootstrap-fill' },
  { label: 'Bootstrap Icons', version: '1.11.1', icon: 'bi-grid-3x3-gap-fill' },
];

function validateHTML(html) {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelectorAll('parsererror').length > 0 ? 'HTML parse error detected.' : null;
  } catch (_) {
    return 'HTML validation failed.';
  }
}

function validateCSS(css) {
  if (!css.trim()) return null;
  try {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
    document.head.removeChild(style);
    return null;
  } catch (_) {
    return 'CSS validation failed.';
  }
}

// Strip '#' for Monaco color tokens (they don't use it)
const hex = (color) => color?.replace('#', '') || 'ffffff';

export default function HtmlCssWorkspaceModal({ section, dispatch, appId, onClose, page, sectionCount }) {
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const cfg = section.section_config || {};
  const bindings = section.data_binding?.bindings || [];
  const { data: arceeData } = useHtmlCssPreviewData(appId, bindings);

  // 'fullpage' makes the published iframe fill the viewport and scroll itself —
  // required for pasted full websites, whose 100vh heroes, fixed navbars and
  // #anchor navigation only work when the iframe is the scroll container.
  const isFullPage = cfg.render_mode === 'fullpage';

  const primary      = theme.primary      || '#4e2e80';
  const secondary    = theme.secondary    || '#ff7a59';
  const gradient     = theme.gradientBackground || `linear-gradient(90deg, ${primary}, ${secondary})`;
  const primaryLight = theme.primaryLight || primary;
  const btnRadius    = theme.buttonRadius || '0.5rem';

  // Snapshot on open so Cancel can revert
  const originalConfig = useRef({
    html: cfg.html || '', css: cfg.css || '', js: cfg.js || '',
    render_mode: cfg.render_mode || 'inline',
    link_navigations: cfg.link_navigations || [],
    bindings: section.data_binding?.bindings || [],
  });

  const [activeTab, setActiveTab]             = useState('html');
  const [validationErrors, setValidationErrors] = useState({});
  const [previewKey, setPreviewKey]           = useState(0);
  const [previewVisible, setPreviewVisible]   = useState(true);
  const [navToast, setNavToast]               = useState('');
  const [showTplSave, setShowTplSave]         = useState(false);
  const [tplName, setTplName]                 = useState('');
  const [savingTpl, setSavingTpl]             = useState(false);
  const tplInputRef = useRef(null);

  // Register a custom Monaco theme based on the active Arcee theme
  const handleEditorWillMount = useCallback((monaco) => {
    monaco.editor.defineTheme('arcee-workspace', {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'comment',       foreground: hex(primaryLight), fontStyle: 'italic' },
        { token: 'keyword',       foreground: hex(primary),      fontStyle: 'bold'   },
        { token: 'tag',           foreground: hex(primary)                            },
        { token: 'attribute.name',foreground: hex(secondary)                          },
        { token: 'string',        foreground: hex(secondary)                          },
        { token: 'number',        foreground: hex(secondary)                          },
      ],
      colors: {
        'editor.background':                   '#ffffff',
        'editor.foreground':                   '#1e293b',
        'editor.lineHighlightBackground':      primary + '12',
        'editor.selectionBackground':          primary + '28',
        'editor.inactiveSelectionBackground':  primary + '14',
        'editorCursor.foreground':             primary,
        'editorLineNumber.foreground':         primary + '60',
        'editorLineNumber.activeForeground':   primary,
        'editorIndentGuide.activeBackground1': primary + '40',
        'editorBracketMatch.background':       secondary + '25',
        'editorBracketMatch.border':           secondary,
        'scrollbarSlider.background':          primary + '28',
        'scrollbarSlider.hoverBackground':     primary + '45',
        'scrollbarSlider.activeBackground':    primary + '65',
        'editorWidget.background':             '#f8fafc',
        'editorWidget.border':                 primary + '40',
        'focusBorder':                         primary,
        'inputOption.activeBorder':            primary,
        'list.highlightForeground':            primary,
        'editorSuggestWidget.highlightForeground': primary,
      },
    });
  }, [primary, secondary, primaryLight]);

  const openTplSave = () => {
    setTplName('');
    setShowTplSave(true);
    setTimeout(() => tplInputRef.current?.focus(), 50);
  };

  const handleSaveTemplate = async () => {
    if (!tplName.trim() || savingTpl) return;
    setSavingTpl(true);
    try {
      await apiHelper.apiCall('/arcee/application/section-template/save/', {
        method: 'POST',
        body: JSON.stringify({
          application_id: parseInt(appId),
          name: tplName.trim(),
          section_type: section.section_type,
          default_config: {
            section_config: section.section_config,
            layout_config:  section.layout_config,
            data_binding:   section.data_binding,
          },
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['sectionTemplates', appId] });
      setShowTplSave(false);
      setTplName('');
    } catch (_) {
      // non-critical
    } finally {
      setSavingTpl(false);
    }
  };

  const update = useCallback((patch) => {
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  }, [dispatch, section._clientId]);

  const handleChange = useCallback((value) => {
    update({ [activeTab]: value || '' });
  }, [activeTab, update]);

  const runValidation = useCallback(() => {
    const errors = {};
    const htmlErr = validateHTML(cfg.html || '');
    const cssErr  = validateCSS(cfg.css  || '');
    if (htmlErr) errors.html = htmlErr;
    if (cssErr)  errors.css  = cssErr;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [cfg.html, cfg.css]);

  const handleRunPreview = useCallback(() => {
    runValidation();
    setPreviewKey(k => k + 1);
    setPreviewVisible(true);
  }, [runValidation]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleRunPreview();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, handleRunPreview]);

  const handleCancel = useCallback(() => {
    update({
      html: originalConfig.current.html,
      css:  originalConfig.current.css,
      js:   originalConfig.current.js,
      render_mode: originalConfig.current.render_mode,
      link_navigations: originalConfig.current.link_navigations,
    });
    dispatch({ type: 'UPDATE_DATA_BINDING', clientId: section._clientId, patch: { bindings: originalConfig.current.bindings } });
    onClose();
  }, [update, dispatch, section._clientId, onClose]);

  // Export this section as the four files a committed client-side page
  // template is made of. Refuses anything four files cannot faithfully
  // represent rather than producing a template that silently drops content.
  // Takes `section` rather than closing over `cfg`, which is a fresh object
  // every render and would rebuild this callback each time.
  const handleDownloadTemplate = useCallback(() => {
    const blocked = templateBlockedReason(page, sectionCount);
    if (blocked) { setNavToast(blocked); return; }
    downloadTemplateZip(buildTemplateFiles(section, page), page.slug);
    setNavToast(`Downloaded ${page.slug}.zip — unzip it into arcee-client/public/pages/`);
  }, [section, page, sectionCount]);

  // Configured links must not navigate the admin away from the builder, so the
  // preview reports the destination instead of following it.
  useEffect(() => {
    function onMessage(e) {
      if (e.data?.type !== 'ARCEE_NAV_PREVIEW') return;
      setNavToast(e.data.href);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!navToast) return undefined;
    const t = setTimeout(() => setNavToast(''), 2600);
    return () => clearTimeout(t);
  }, [navToast]);

  // Mirrors the client's HtmlCssSection full-page anchor handling so the
  // preview scrolls the same way the published page will.
  const anchorShim = isFullPage
    ? `document.addEventListener('click', function(e){
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href === '#') return;
      var target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });`
    : '';

  const iframeSrc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css" />
  <style>
    :root {
      --primary-color: ${primary};
      --secondary-color: ${secondary};
      --gradient-bg: ${gradient};
      --button-radius: ${btnRadius};
    }
    html, body { margin: 0; padding: 0; font-family: sans-serif; }
    ${isFullPage ? 'html, body { height: 100%; }' : ''}
    ${cfg.css || ''}
  </style>
</head>
<body>
  ${cfg.html || ''}
  <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js"><\/script>
  <script>window.ARCEE_DATA = ${serializeArceeData(arceeData || {})};
  window.ARCEE_CARDS = ${serializeArceeData(buildCardConfig(cfg.cards || cfg.card_buttons))};<\/script>
  <script>${buildCardScript()}
  ${cfg.js || ''}
  ${buildPreviewNavScript(cfg.link_navigations || [])}
  ${anchorShim}<\/script>
</body>
</html>`;

  const currentValue = cfg[activeTab] || '';
  const currentLang  = TABS.find(t => t.key === activeTab)?.language || 'html';
  const hasErrors    = Object.keys(validationErrors).length > 0;

  // Shared button base style
  const toolBtn = (overrides = {}) => ({
    display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    borderRadius: btnRadius, border: 'none',
    padding: '5px 13px', transition: 'opacity 0.15s',
    ...overrides,
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8fafc',
      }}
    >
      {/* ── Gradient top toolbar ─────────────────────────────────────────────── */}
      <div
        style={{
          background: gradient,
          flexShrink: 0,
          boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
          zIndex: 1,
        }}
      >
        {/* Row 1: title + action buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            height: 50,
            gap: 8,
          }}
        >
          {/* Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <div
              style={{
                width: 30, height: 30, borderRadius: 8,
                background: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <i className="bi bi-code-slash" style={{ color: '#fff', fontSize: 15 }} />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                HTML / CSS / JS Workspace
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, lineHeight: 1.2 }}>
                Full-screen code editor with live preview
              </div>
            </div>

            {/* Available library badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 10 }}>
              {SANDBOX_LIBS.map(lib => (
                <span
                  key={lib.label}
                  title={`${lib.label} v${lib.version} is available in the preview sandbox`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.28)',
                    borderRadius: 20,
                    padding: '2px 9px',
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.02em',
                  }}
                >
                  <i className={`bi ${lib.icon}`} style={{ fontSize: 10 }} />
                  {lib.label} <span style={{ opacity: 0.75 }}>v{lib.version}</span>
                </span>
              ))}

              {/* Render mode — inline (flows with the page) vs full page */}
              <button
                type="button"
                onClick={() => update({ render_mode: isFullPage ? 'inline' : 'fullpage' })}
                title={
                  isFullPage
                    ? 'Full page: the section fills the viewport and scrolls itself. Use for complete websites — 100vh heroes, fixed navbars and #anchor links work normally.'
                    : 'Inline: the section is auto-sized to its content and flows with the rest of the page.'
                }
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: isFullPage ? '#fff' : 'rgba(255,255,255,0.15)',
                  border: `1px solid ${isFullPage ? '#fff' : 'rgba(255,255,255,0.28)'}`,
                  borderRadius: 20,
                  padding: '2px 9px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: isFullPage ? primary : '#fff',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  marginLeft: 4,
                }}
              >
                <i
                  className={`bi ${isFullPage ? 'bi-fullscreen' : 'bi-arrows-collapse'}`}
                  style={{ fontSize: 10 }}
                />
                {isFullPage ? 'Full page' : 'Inline'}
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Toggle preview */}
            <button
              type="button"
              onClick={() => setPreviewVisible(v => !v)}
              style={toolBtn({
                background: previewVisible ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
              })}
              title="Toggle preview pane"
            >
              <i className="bi bi-layout-split" />
              Preview
            </button>

            {/* Run */}
            <button
              type="button"
              onClick={handleRunPreview}
              style={toolBtn({ background: 'rgba(255,255,255,0.22)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' })}
              title="Run preview (Ctrl+Enter)"
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.32)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
            >
              <i className="bi bi-play-fill" />
              Run
            </button>

            {/* Validate */}
            <button
              type="button"
              onClick={runValidation}
              style={toolBtn({
                background: hasErrors ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.12)',
                color: hasErrors ? '#fca5a5' : 'rgba(255,255,255,0.85)',
                border: hasErrors ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.25)',
              })}
              title="Validate HTML & CSS"
            >
              <i className={`bi ${hasErrors ? 'bi-x-circle' : 'bi-check2-circle'}`} />
              Validate
            </button>

            {/* Download as page template */}
            <button
              type="button"
              onClick={handleDownloadTemplate}
              style={toolBtn({ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)' })}
              title="Download this section as index.html / style.css / script.js / config.json"
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <i className="bi bi-download" />
              Download
            </button>

            {/* Save as Template */}
            <button
              type="button"
              onClick={openTplSave}
              style={toolBtn({ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)' })}
              title="Save this section as a reusable template"
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <i className="bi bi-bookmark-plus" />
              Save as Template
            </button>

            {/* Separator */}
            <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.3)', margin: '0 2px' }} />

            {/* Done */}
            <button
              type="button"
              onClick={onClose}
              style={toolBtn({ background: '#fff', color: primary, border: 'none', fontWeight: 700 })}
              title="Save and close (Escape)"
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <i className="bi bi-check-lg" />
              Done
            </button>

            {/* Cancel */}
            <button
              type="button"
              onClick={handleCancel}
              style={toolBtn({ background: 'transparent', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.3)' })}
              title="Cancel and revert changes"
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <i className="bi bi-x-lg" />
              Cancel
            </button>
          </div>
        </div>

        {/* Row 2: template name input (only when active) */}
        {showTplSave && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 16px',
              background: 'rgba(0,0,0,0.12)',
              borderTop: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <i className="bi bi-bookmark" style={{ color: '#fff', fontSize: 12 }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              Save Section as Template:
            </span>
            <input
              ref={tplInputRef}
              value={tplName}
              onChange={e => setTplName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter')  handleSaveTemplate();
                if (e.key === 'Escape') { setShowTplSave(false); setTplName(''); }
              }}
              placeholder="Template name…"
              style={{
                flex: 1,
                maxWidth: 280,
                padding: '3px 10px',
                borderRadius: btnRadius,
                border: '1px solid rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontSize: 12,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={handleSaveTemplate}
              disabled={savingTpl || !tplName.trim()}
              style={toolBtn({ background: '#fff', color: primary, border: 'none', fontWeight: 700 })}
            >
              {savingTpl ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowTplSave(false); setTplName(''); }}
              style={toolBtn({ background: 'transparent', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.3)' })}
            >
              ✕
            </button>
          </div>
        )}

        {/* Row 3: language tabs (sit on top of the white editor area) */}
        <div style={{ display: 'flex', padding: '0 16px', gap: 2 }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const hasErr   = Boolean(validationErrors[tab.key]);
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '7px 18px 6px',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  background: isActive ? '#fff' : 'rgba(255,255,255,0.15)',
                  color: isActive ? primary : 'rgba(255,255,255,0.8)',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'background 0.12s, color 0.12s',
                  boxShadow: isActive ? '0 -1px 6px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                <i className={`bi ${tab.icon}`} style={{ fontSize: 13 }} />
                {tab.label}
                {hasErr && (
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#ef4444',
                    display: 'inline-block',
                    boxShadow: '0 0 0 2px rgba(239,68,68,0.3)',
                  }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Main body: editor + preview ─────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Editor pane */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            borderTop: `3px solid ${primary}`,
            background: '#fff',
          }}
        >
          {activeTab === 'bindings' ? (
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <HtmlCssDataBindingsPanel
                appId={appId}
                bindings={section.data_binding?.bindings || []}
                onChange={(bindings) => dispatch({ type: 'UPDATE_DATA_BINDING', clientId: section._clientId, patch: { bindings } })}
              />
            </div>
          ) : activeTab === 'cards' ? (
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <HtmlCssCardsPanel
                appId={appId}
                bindings={section.data_binding?.bindings || []}
                cardButtons={cfg.cards || cfg.card_buttons || []}
                onChange={(cards) => update({ cards })}
              />
            </div>
          ) : activeTab === 'links' ? (
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <HtmlCssLinkNavPanel
                appId={appId}
                html={cfg.html || ''}
                navigations={cfg.link_navigations || []}
                onChange={(link_navigations) => update({ link_navigations })}
              />
            </div>
          ) : (
            <Editor
              height="100%"
              language={currentLang}
              value={currentValue}
              onChange={handleChange}
              theme="arcee-workspace"
              beforeMount={handleEditorWillMount}
              options={{
                minimap: { enabled: true, scale: 1 },
                fontSize: 14,
                lineHeight: 22,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                automaticLayout: true,
                tabSize: 2,
                renderWhitespace: 'selection',
                bracketPairColorization: { enabled: true },
                formatOnPaste: true,
                suggestOnTriggerCharacters: true,
                quickSuggestions: true,
                padding: { top: 14, bottom: 14 },
                scrollbar: {
                  verticalScrollbarSize: 8,
                  horizontalScrollbarSize: 8,
                },
                overviewRulerBorder: false,
                hideCursorInOverviewRuler: true,
                renderLineHighlight: 'gutter',
              }}
            />
          )}
        </div>

        {/* Divider */}
        {previewVisible && (
          <div
            style={{
              width: 3,
              background: gradient,
              flexShrink: 0,
              opacity: 0.5,
            }}
          />
        )}

        {/* Preview pane */}
        {previewVisible && (
          <div
            style={{
              width: '42%',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              background: '#fff',
              borderTop: `3px solid ${secondary}`,
            }}
          >
            {/* Preview toolbar */}
            <div
              style={{
                height: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
                background: primary,
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <i className="bi bi-display" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  Live Preview
                </span>
              </div>
              <button
                type="button"
                onClick={handleRunPreview}
                style={{
                  padding: '3px 10px',
                  background: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: btnRadius,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                title="Refresh preview"
              >
                <i className="bi bi-arrow-clockwise" />
                Refresh
              </button>
            </div>

            {/* Preview iframe */}
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
              <iframe
                key={previewKey}
                srcDoc={iframeSrc}
                sandbox="allow-scripts allow-forms"
                style={{ border: 'none', width: '100%', height: '100%', display: 'block' }}
                title="HTML/CSS/JS Preview"
              />

              {/* Configured-link feedback — the preview reports the destination
                  rather than navigating, so the builder is never left. */}
              {navToast && (
                <div
                  style={{
                    position: 'absolute', left: 12, right: 12, bottom: 12,
                    background: 'rgba(15,23,42,0.92)', color: '#fff',
                    borderRadius: btnRadius, padding: '8px 12px',
                    fontSize: 11, display: 'flex', alignItems: 'center', gap: 7,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                  }}
                >
                  <i className="bi bi-signpost-2" style={{ color: secondary }} />
                  <span style={{ opacity: 0.8 }}>On the live page this navigates to</span>
                  <code style={{ color: '#fff', fontWeight: 700 }}>{navToast}</code>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Status bar ──────────────────────────────────────────────────────── */}
      <div
        style={{
          height: 26,
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          gap: 20,
          flexShrink: 0,
        }}
      >
        {['html', 'css'].map(key => (
          <span
            key={key}
            style={{
              fontSize: 11,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              opacity: validationErrors[key] ? 1 : 0.85,
              fontWeight: validationErrors[key] ? 600 : 400,
            }}
          >
            <i
              className={`bi ${validationErrors[key] ? 'bi-x-circle-fill' : 'bi-check-circle-fill'}`}
              style={{ color: validationErrors[key] ? '#fca5a5' : 'rgba(255,255,255,0.9)' }}
            />
            {key.toUpperCase()} {validationErrors[key] ? `— ${validationErrors[key]}` : 'OK'}
          </span>
        ))}
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-check-circle-fill" style={{ color: 'rgba(255,255,255,0.9)' }} />
          JS — Monaco inline validation
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
          Ctrl+Enter — run preview &nbsp;·&nbsp; Escape — close
        </span>
      </div>
    </div>
  );
}
